import { NO, UIColor, UIRoute, UITextView, UIView, YES } from "uicore-ts"
// @ts-ignore
import { RHProcurementPartOffer } from "../../../../models/OfferModels"
import { RHProcurementPart, RHRequiredDocument } from "../../../../models/ProcurementModels"
import { RHFeatureFlags } from "../../Core/RHFeatureFlags"
import { CBButtonGroup } from "../../Custom components/CB/CBButtonGroup"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHColor } from "../../Custom components/RH/RHColor"
import { RHFileListView } from "../../Custom components/RH/RHFileListView"
import { RHRadioButton } from "../../Custom components/RH/RHRadioButton"
import { RHTextArea } from "../../Custom components/RH/RHTextArea"
import { RHButtons, RHColors, RHFont, RHNavbar, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHProcurementPartDisplay } from "../../Custom components/RH/Utils_slop/RHProcurementPartDisplay"
import { RHTextSanitizer } from "../../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHOfferDetailDialogViewTokens } from "../../Custom components/RH/Utils_slop/RHViewTokens"
import { OfferMessageThreadView } from "../MakeOffer/OfferMessageThreadView"
import { populatedValuesOfReferences } from "../../Core/RHReference"

const MEETS_REQUIREMENTS_YES_KEY = "yes"
const MEETS_REQUIREMENTS_NO_KEY = "no"
const EVALUATION_NOTES_TEXTAREA_ID = "offer-detail-evaluation-notes-textarea"


// ─────────────────────────────────────────────────────────────────────────────
// Evaluation card: radio pair (side by side) on top, notes textarea below, shared border
// ─────────────────────────────────────────────────────────────────────────────

class EvaluationCardView extends UIView {
    
    readonly titleLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            text: "Hindamine",
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: RHFont.sizeLarge,
                fontWeight: RHOfferDetailDialogViewTokens.sectionTitleFontWeight,
                lineHeight: RHFont.lineHeightHeading
            }
        })
        .addedAsSubviewToView(this)
    
    readonly meetsRequirementsYesRadio = new RHRadioButton()
        .configuredWithObject({
            titleLabel: { text: "Vastab hanketingimustele" }
        })
        .addedAsSubviewToView(this)
    
    readonly meetsRequirementsNoRadio = new RHRadioButton()
        .configuredWithObject({
            titleLabel: { text: "Ei vasta hanketingimustele" }
        })
        .addedAsSubviewToView(this)
    
    readonly meetsRequirementsGroup = new CBButtonGroup({
        [MEETS_REQUIREMENTS_YES_KEY]: this.meetsRequirementsYesRadio,
        [MEETS_REQUIREMENTS_NO_KEY]: this.meetsRequirementsNoRadio
    })
    
    readonly notesLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            text: "Selgitus",
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: RHFont.sizeLarge,
                fontWeight: RHOfferDetailDialogViewTokens.sectionTitleFontWeight,
                lineHeight: RHFont.lineHeightHeading
            }
        })
        .addedAsSubviewToView(this)
    
    readonly notesTextArea = new RHTextArea(EVALUATION_NOTES_TEXTAREA_ID)
        .configuredWithObject({
            placeholderText: "Kirjuta selgitus"
        })
        .addedAsSubviewToView(this)
    
    override _focusManager = new RHFocusManager(() => {
        const items: UIView[] = []
        if (this.meetsRequirementsYesRadio.enabled) { items.push(this.meetsRequirementsYesRadio) }
        if (this.meetsRequirementsNoRadio.enabled) { items.push(this.meetsRequirementsNoRadio) }
        if (this.notesTextArea.enabled) { items.push(this.notesTextArea) }
        return items
    })
    
    constructor(elementID?: string) {
        super(elementID);
        (this as EvaluationCardView).configureWithObject({
            style: {
                borderRadius: "8px",
                border: `${RHOfferDetailDialogViewTokens.evaluationCardBorderWidth} solid ${RHNavbar.topBarTopBarBackground.stringValue}`
            }
        })

        UIView.injectCSS(`
            #${EVALUATION_NOTES_TEXTAREA_ID}::placeholder,
            #${EVALUATION_NOTES_TEXTAREA_ID}_textElement::placeholder {
                color: ${RHColors.textPlaceholder.stringValue} !important;
                -webkit-text-fill-color: ${RHColors.textPlaceholder.stringValue} !important;
                opacity: 1;
            }
        `, "rh-offer-detail-evaluation-notes-placeholder")
    }
    
    
    updateBorderForMeetsRequirements(meetsRequirements: boolean | undefined) {
        let borderColor = RHNavbar.topBarTopBarBackground.stringValue
        
        if (meetsRequirements === YES) {
            borderColor = RHColor.success.stringValue
        }
        else if (meetsRequirements === NO) {
            borderColor = RHColor.danger.stringValue
        }

        (this as EvaluationCardView).configureWithObject({
            style: { border: `${RHOfferDetailDialogViewTokens.evaluationCardBorderWidth} solid ${borderColor}` }
        })
    }
    
    
    private _notesTextAreaHeight(constrainingWidth: number = 0): number {
        const contentInset = RHSpacing.medium
        let textAreaWidth = constrainingWidth - contentInset * 2
        
        if (textAreaWidth <= 0) {
            textAreaWidth = this.bounds.width - contentInset * 2
        }
        if (textAreaWidth <= 0) {
            textAreaWidth = 0
        }
        
        return [
            this.notesTextArea.intrinsicContentHeight(textAreaWidth),
            88
        ].max()
    }
    
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const contentInset = RHSpacing.medium
        const titleHeight = 28
        const radioHeight = 32
        const notesTextAreaHeight = this._notesTextAreaHeight(this.bounds.width)
        const bounds = this.contentBoundsWithInset(contentInset)
        
        // Title spanning full width
        const radiosFrame = bounds
            .rectangleWithHeight(titleHeight)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(
                RHSpacing.small,
                radioHeight
            )
        
        // Radios side by side on one row
        radiosFrame
            .rectangleWithMaxWidth(500, 0)
            .distributeViewsEquallyAlongWidth(
                [this.meetsRequirementsYesRadio, this.meetsRequirementsNoRadio],
                RHSpacing.medium
            )
        
        // Notes label + textarea below the radios
        radiosFrame
            .rectangleForNextRow(
                RHSpacing.medium,
                titleHeight
            )
            .assignedAsFrameOfView(this.notesLabel)
            .rectangleForNextRow(
                RHSpacing.tiny,
                notesTextAreaHeight
            )
            .assignedAsFrameOfView(this.notesTextArea)
    }
    
}


const OFFER_DETAIL_ROUTE_COMPONENT_NAME = "offerDetail"


// ─────────────────────────────────────────────────────────────────────────────
// Offer detail dialog
// ─────────────────────────────────────────────────────────────────────────────

export class OfferDetailDialogView extends UIView {
    
    // ── Header: company + part label ─────────────────────────────────────────
    
    readonly companyLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: RHFont.sizeXXLarge,
                fontWeight: "400",
                lineHeight: RHFont.lineHeightHeading
            }
        })
        .addedAsSubviewToView(this)
    
    readonly partLabel = new UITextView()
        .configuredWithObject({
            textColor: RHColor.textSecondary,
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: RHFont.sizeSmall,
                lineHeight: RHFont.lineHeightBase
            }
        })
        .addedAsSubviewToView(this)
    
    // ── Price ─────────────────────────────────────────────────────────────────
    
    readonly priceSectionLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            text: "Maksumus (KM-ta)",
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: RHFont.sizeLarge,
                fontWeight: RHOfferDetailDialogViewTokens.sectionTitleFontWeight,
                lineHeight: RHFont.lineHeightHeading
            }
        })
        .addedAsSubviewToView(this)
    
    readonly priceValueLabel = new UITextView()
        .configuredWithObject({
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: RHFont.sizeMedium,
                fontWeight: "700",
                lineHeight: RHFont.lineHeightBase
            }
        })
        .addedAsSubviewToView(this)
    
    // ── Comments (optional) ───────────────────────────────────────────────────
    
    readonly commentsSectionLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            text: "Kommentaar",
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: RHFont.sizeLarge,
                fontWeight: RHOfferDetailDialogViewTokens.sectionTitleFontWeight,
                lineHeight: RHFont.lineHeightHeading
            }
        })
    
    readonly commentsValueLabel = new UITextView()
        .configuredWithObject({
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: RHFont.sizeSmall,
                fontStyle: "italic",
                lineHeight: RHFont.lineHeightBase
            }
        })
    
    // ── Required documents ────────────────────────────────────────────────────
    
    readonly requiredDocumentsSectionLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            text: "Nõutud dokumendid",
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: RHFont.sizeLarge,
                fontWeight: RHOfferDetailDialogViewTokens.sectionTitleFontWeight,
                lineHeight: RHFont.lineHeightHeading
            }
        })
        .addedAsSubviewToView(this)
    
    private _requiredDocumentRows: { labelView: UITextView, fileListView: RHFileListView }[] = []
    
    readonly noRequiredDocumentsLabel = new UITextView()
        .configuredWithObject({
            text: "Nõutud dokumente ei ole",
            textColor: RHColor.textSecondary,
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: RHFont.sizeSmall,
                fontStyle: "italic",
                lineHeight: RHFont.lineHeightBase
            }
        })
    
    // ── Regular files ─────────────────────────────────────────────────────────
    
    readonly filesSectionLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            text: "Lisafailid",
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: RHFont.sizeLarge,
                fontWeight: RHOfferDetailDialogViewTokens.sectionTitleFontWeight,
                lineHeight: RHFont.lineHeightHeading
            }
        })
        .addedAsSubviewToView(this)
    
    readonly filesListView = new RHFileListView()
        .addedAsSubviewToView(this)
    
    readonly noFilesLabel = new UITextView()
        .configuredWithObject({
            text: "Lisafaile ei ole",
            textColor: RHColor.textSecondary,
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: RHFont.sizeSmall,
                fontStyle: "italic",
                lineHeight: RHFont.lineHeightBase
            }
        })
    
    // ── Evaluation card ───────────────────────────────────────────────────────
    
    readonly evaluationCard = new EvaluationCardView()
        .addedAsSubviewToView(this)
    
    // ── Messaging ─────────────────────────────────────────────────────────────
    
    readonly messagingSectionLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            // Emphasise that this offer thread is private (1:1 procurer ↔ offerer),
            // unlike the public "Teabevahetus" Q&A.
            text: "<b>Privaatsed</b> sõnumid",
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: RHFont.sizeLarge,
                fontWeight: RHOfferDetailDialogViewTokens.sectionTitleFontWeight,
                lineHeight: RHFont.lineHeightHeading
            }
        })
        .addedAsSubviewToView(this)
    
    readonly messageThreadView = new OfferMessageThreadView()
        .addedAsSubviewToView(this)
    
    // ── Footer ────────────────────────────────────────────────────────────────
    
    readonly prevButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "← Eelmine" },
            variant: "softWarning",
            contentPadding: RHButtons.compactContentPadding,
            style: {
                fontSize: RHButtons.compactFontSize
            }
        })
        .addedAsSubviewToView(this)
    
    readonly nextButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Järgmine →" },
            variant: "primary",
            contentPadding: RHButtons.compactContentPadding,
            style: {
                fontSize: RHButtons.compactFontSize
            }
        })
        .addedAsSubviewToView(this)
    
    readonly closeButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Sulge" },
            variant: "softDanger",
            contentPadding: RHButtons.compactContentPadding,
            style: {
                fontSize: RHButtons.compactFontSize
            }
        })
        .addedAsSubviewToView(this)
    
    readonly footerBottomSpacerView = new UIView()
        .addedAsSubviewToView(this)
    
    // ── State ─────────────────────────────────────────────────────────────────
    
    override _focusManager = new RHFocusManager(() => {
        const items: (UIView | RHFocusManager)[] = []
        if (this.evaluationCard._focusManager) {
            items.push(this.evaluationCard._focusManager)
        }
        if (this.messageThreadView.superview === this) {
            items.push(this.messageThreadView.inputArea)
            items.push(this.messageThreadView.sendButton)
        }
        if (this.prevButton.enabled) { items.push(this.prevButton) }
        items.push(this.closeButton)
        if (this.nextButton.enabled) { items.push(this.nextButton) }
        return items as UIView[]
    })
    
    private _partOffers: RHProcurementPartOffer[] = []
    private _currentIndex: number = 0
    private _part?: RHProcurementPart
    private _partCount?: number
    private _isEvaluationFinalizable: boolean = NO
    _evaluationDidChange?: () => void
    private _persistPartOfferEvaluation?: (partOffer: RHProcurementPartOffer, onError?: () => void) => Promise<void>
    // Invoked when this dialog demotes the currently-displayed offer away from
    // "chosenAsWinner" (because it no longer meets requirements), so the
    // section that owns the live winner-selection state can clear it too.
    _winnerWasCleared?: (clearedPartOfferId: string) => void
    // Invoked if that demotion's server write is then rejected, so the section
    // can restore the winner it optimistically cleared.
    _winnerWasRestored?: (restoredPartOfferId: string) => void
    // The offer package ID — stable for the lifetime of this dialog open session.
    // We configure the thread once in show() and never reload on prev/next navigation.
    private _procurementOfferId?: string
    private _canAnswerMessages = NO
    
    constructor(elementID?: string) {
        super(elementID);
        (this as OfferDetailDialogView).configureWithObject({
            backgroundColor: RHColor.surface,
            style: {
                borderRadius: "12px"
            }
        })
        
        this._updateMessagingVisibility()
        
        this.evaluationCard.meetsRequirementsGroup.selectionDidChange = (_, value) => {
            const partOffer = this._currentPartOffer
            if (!partOffer || !this._isEvaluationFinalizable) {
                return
            }
            if (value === MEETS_REQUIREMENTS_YES_KEY) {
                partOffer.meetsRequirements = YES
            }
            else if (value === MEETS_REQUIREMENTS_NO_KEY) {
                partOffer.meetsRequirements = NO
            }
            else {
                partOffer.meetsRequirements = undefined
            }
            // An offer that no longer meets requirements cannot stay the chosen
            // winner — mirrors the same rule enforced by the winner checkbox in
            // the main evaluation table (RHPartOfferEvaluationSection). If the
            // server rejects this write, persistPartOfferEvaluation reverts
            // partOffer's own fields on its own; restoredWinnerPartOfferId lets
            // us also tell the section to restore _selectedOfferId in that case.
            let restoredWinnerPartOfferId: string | undefined
            if (!partOffer.meetsRequirements && partOffer.decision === "chosenAsWinner") {
                partOffer.decision = "undecided"
                const clearedPartOfferId = partOffer._id?.toString()
                if (clearedPartOfferId) {
                    this._winnerWasCleared?.(clearedPartOfferId)
                    restoredWinnerPartOfferId = clearedPartOfferId
                }
            }
            void this._persistPartOfferEvaluation?.(partOffer, () => {
                if (restoredWinnerPartOfferId) {
                    this._winnerWasRestored?.(restoredWinnerPartOfferId)
                }
            })
            this._evaluationDidChange?.()
            this.evaluationCard.updateBorderForMeetsRequirements(partOffer.meetsRequirements)
        }
        
        // noinspection JSConstantReassignment
        this.evaluationCard.notesTextArea.controlEventTargetAccumulator.TextChange = (sender: UIView) => {
            const textArea = sender as RHTextArea
            const partOffer = this._currentPartOffer
            if (!partOffer || !this._isEvaluationFinalizable) {
                return
            }
            partOffer.evaluationNotes = textArea.text
            this.evaluationCard.setNeedsLayoutUpToRootView()
        }
        
        // noinspection JSConstantReassignment
        this.evaluationCard.notesTextArea.controlEventTargetAccumulator.Blur = () => {
            const partOffer = this._currentPartOffer
            if (partOffer && this._isEvaluationFinalizable) {
                this._persistPartOfferEvaluation?.(partOffer)
            }
        }
        
        // noinspection JSConstantReassignment
        this.evaluationCard.notesTextArea.controlEventTargetAccumulator.EnterDown = (_sender: UIView, event: Event) => {
            if (event instanceof KeyboardEvent && (event.ctrlKey || event.metaKey)) {
                this.evaluationCard.notesTextArea.blur()
            }
        }
        
        // noinspection JSConstantReassignment
        this.prevButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._navigateToIndex(this._currentIndex - 1)
        }
        
        // noinspection JSConstantReassignment
        this.nextButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._navigateToIndex(this._currentIndex + 1)
        }
        
        // noinspection JSConstantReassignment
        this.closeButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            UIRoute.currentRoute
                .routeByRemovingComponentNamed(OFFER_DETAIL_ROUTE_COMPONENT_NAME)
                .apply()
        }
    }
    
    configure(
        partOffers: RHProcurementPartOffer[],
        initialIndex: number,
        part: RHProcurementPart,
        isEvaluationFinalizable: boolean,
        evaluationDidChange: () => void,
        persistPartOfferEvaluation: (partOffer: RHProcurementPartOffer, onError?: () => void) => Promise<void>,
        procurementOfferId?: string,
        partCount?: number,
        winnerWasCleared?: (clearedPartOfferId: string) => void,
        winnerWasRestored?: (restoredPartOfferId: string) => void,
        canAnswerMessages: boolean = NO
    ) {
        this._partOffers = partOffers
        this._part = part
        this._partCount = partCount
        this._isEvaluationFinalizable = isEvaluationFinalizable
        this._evaluationDidChange = evaluationDidChange
        this._persistPartOfferEvaluation = persistPartOfferEvaluation
        this._procurementOfferId = procurementOfferId
        this._winnerWasCleared = winnerWasCleared
        this._winnerWasRestored = winnerWasRestored
        this._canAnswerMessages = canAnswerMessages
        this._updateMessagingVisibility()
        
        this._navigateToIndex(initialIndex)
    }

    private _updateMessagingVisibility() {
        if (!RHFeatureFlags.offerMessagingIsVisible || !this._canAnswerMessages) {
            this.messagingSectionLabel.removeFromSuperview()
            this.messageThreadView.removeFromSuperview()
            return
        }

        this.messagingSectionLabel.addedAsSubviewToView(this)
        this.messageThreadView.addedAsSubviewToView(this)
    }
    
    private get _currentPartOffer(): RHProcurementPartOffer | undefined {
        return this._partOffers[this._currentIndex]
    }
    
    private _navigateToIndex(index: number) {
        this._currentIndex = Math.max(0, Math.min(index, this._partOffers.length - 1))
        this._populateContent()
        this.setNeedsLayout()
    }
    
    private _populateContent() {
        const partOffer = this._currentPartOffer
        if (!partOffer) {
            return
        }
        
        const companyName = partOffer.offererCompanyName
            ?? "Tundmatu ettevõte"
        this.companyLabel.text = RHTextSanitizer.escapedHTML(companyName)
        
        const partText = this._part
                         ? RHProcurementPartDisplay.partHeaderText(this._part, this._partCount)
                         : ""
        this.partLabel.text = partText
        if (partText) {
            this.partLabel.hidden = NO
        }
        else {
            this.partLabel.hidden = YES
        }
        
        if (partOffer.price?.amount !== undefined) {
            this.priceValueLabel.text = RHEuroFormatter.formattedCurrency(partOffer.price.amount, {
                currency: partOffer.price.currency || "EUR",
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            })
        }
        else {
            this.priceValueLabel.text = "—"
        }
        
        this.commentsSectionLabel.removeFromSuperview()
        this.commentsValueLabel.removeFromSuperview()
        
        this._buildRequiredDocumentRows(partOffer)
        
        // Regular files (optional section)
        const regularFiles = populatedValuesOfReferences(partOffer.files)
        if (regularFiles.length > 0) {
            this.filesListView.addedAsSubviewToView(this)
            this.noFilesLabel.removeFromSuperview()
            this.filesListView.configureWithObject({
                files: regularFiles,
                downloadEnabled: YES,
                deleteEnabled: NO
            })
        }
        else {
            this.filesListView.removeFromSuperview()
            this.noFilesLabel.addedAsSubviewToView(this)
        }
        
        // Sync radio group to current evaluation state without triggering selectionDidChange
        const meetsRequirements = partOffer.meetsRequirements
        if (meetsRequirements === YES) {
            this.evaluationCard.meetsRequirementsGroup.value = MEETS_REQUIREMENTS_YES_KEY
        }
        else if (meetsRequirements === NO) {
            this.evaluationCard.meetsRequirementsGroup.value = MEETS_REQUIREMENTS_NO_KEY
        }
        else {
            this.evaluationCard.meetsRequirementsYesRadio.selected = NO
            this.evaluationCard.meetsRequirementsNoRadio.selected = NO
        }
        
        this.evaluationCard.meetsRequirementsYesRadio.enabled = this._isEvaluationFinalizable
        this.evaluationCard.meetsRequirementsNoRadio.enabled = this._isEvaluationFinalizable
        this.evaluationCard.notesTextArea.text = partOffer.evaluationNotes ?? ""
        this.evaluationCard.notesTextArea.enabled = this._isEvaluationFinalizable
        this.evaluationCard.updateBorderForMeetsRequirements(meetsRequirements)
        
        this.prevButton.enabled = this._currentIndex > 0
        this.nextButton.enabled = this._currentIndex < this._partOffers.length - 1
        
        // Configure the message thread for the current part — each part has its own thread.
        // Skipped while messaging is hidden: configure() would retrieve the thread
        // and mark its messages as read server-side, which must not happen invisibly.
        if (this._procurementOfferId && this._canAnswerMessages && this.messageThreadView.superview === this) {
            const partIdStr = partOffer.partId!.toString()
            void this.messageThreadView.configure(this._procurementOfferId, "procurer", partIdStr)
        }
    }
    
    private _buildRequiredDocumentRows(partOffer: RHProcurementPartOffer) {
        for (const row of this._requiredDocumentRows) {
            row.labelView.removeFromSuperview()
            row.fileListView.removeFromSuperview()
        }
        this._requiredDocumentRows = []
        this.noRequiredDocumentsLabel.removeFromSuperview()
        
        const requiredDocuments: RHRequiredDocument[] = this._part?.requiredDocuments || []
        const requiredDocumentFileIds = partOffer.requiredDocumentFileIds ?? {}
        
        const uploadedSlots = requiredDocuments.filter(doc =>
            [requiredDocumentFileIds[doc._id] ?? []].flat().length > 0
        )
        
        if (uploadedSlots.length === 0) {
            this.noRequiredDocumentsLabel.addedAsSubviewToView(this)
            return
        }
        
        for (const requiredDocument of uploadedSlots) {
            const fileIds = [requiredDocumentFileIds[requiredDocument._id] ?? []].flat()
            
            const labelView = new UITextView()
                .configuredWithObject({
                    text: requiredDocument.name,
                    textColor: RHColor.textSecondary,
                    backgroundColor: UIColor.transparentColor,
                    style: {
                        fontSize: RHFont.sizeXSmall,
                        fontWeight: "400",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        lineHeight: RHFont.lineHeightBase
                    }
                })
                .addedAsSubviewToView(this) as UITextView
            
            const populatedFiles = [partOffer.requiredDocumentFiles?.[requiredDocument._id] ?? []].flat()
            const files = fileIds.map(fileId =>
                populatedFiles.find(file => file._id?.toString() === fileId) ??
                { id: fileId, name: requiredDocument.name }
            )
            
            const fileListView = new RHFileListView()
                .configuredWithObject({
                    files,
                    downloadEnabled: YES,
                    deleteEnabled: NO
                })
                .addedAsSubviewToView(this) as RHFileListView
            
            this._requiredDocumentRows.push({ labelView, fileListView })
        }
    }
    
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const contentInset = RHSpacing.large
        const sectionTopGap = RHSpacing.medium
        const fieldTopGap = RHSpacing.tiny
        const sectionTitleHeight = 28
        const valueHeight = 28
        const bounds = this.contentBoundsWithInset(contentInset)
        
        bounds
            // Header
            .rectangleWithHeight(36)
            .assignedAsFrameOfView(this.companyLabel)
            .IF(this.partLabel.hidden === NO)
            .rectangleForNextRow(
                RHSpacing.tiny,
                24
            )
            .assignedAsFrameOfView(this.partLabel)
            .ENDIF()
            
            // Price
            .rectangleForNextRow(sectionTopGap, sectionTitleHeight)
            .assignedAsFrameOfView(this.priceSectionLabel)
            .rectangleForNextRow(fieldTopGap, valueHeight)
            .assignedAsFrameOfView(this.priceValueLabel)
            
            // Comments (optional)
            .IF(this.commentsSectionLabel.superview === this)
            .rectangleForNextRow(sectionTopGap, sectionTitleHeight)
            .assignedAsFrameOfView(this.commentsSectionLabel)
            .rectangleForNextRow(fieldTopGap, this.commentsValueLabel)
            .assignedAsFrameOfView(this.commentsValueLabel)
            .ENDIF()
            
            // Required documents
            .rectangleForNextRow(sectionTopGap, sectionTitleHeight)
            .assignedAsFrameOfView(this.requiredDocumentsSectionLabel)
            
            .IF(this.noRequiredDocumentsLabel.superview === this)
            .rectangleForNextRow(fieldTopGap, valueHeight)
            .assignedAsFrameOfView(this.noRequiredDocumentsLabel)
            .ELSE()
            .rectangleForNextRow(
                fieldTopGap,
                RHOfferDetailDialogViewTokens.requiredDocumentLabelHeight
            )
            .ENDIF((current) => {
                let frame = current
                for (const [rowIndex, row] of this._requiredDocumentRows.entries()) {
                    if (rowIndex > 0) {
                        frame = frame.rectangleForNextRow(
                            RHSpacing.small,
                            RHOfferDetailDialogViewTokens.requiredDocumentLabelHeight
                        )
                    }
                    frame = frame
                        .assignedAsFrameOfView(row.labelView)
                        .rectangleForNextRow(fieldTopGap, row.fileListView)
                        .assignedAsFrameOfView(row.fileListView)
                }
                return frame
            })
            
            // Regular files
            .rectangleForNextRow(sectionTopGap, sectionTitleHeight)
            .assignedAsFrameOfView(this.filesSectionLabel)
            
            .IF(this.noFilesLabel.superview === this)
            .rectangleForNextRow(fieldTopGap, valueHeight)
            .assignedAsFrameOfView(this.noFilesLabel)
            .ELSE()
            .rectangleForNextRow(fieldTopGap, this.filesListView)
            .assignedAsFrameOfView(this.filesListView)
            .ENDIF()
            
            // Evaluation card — intrinsic height comes from EvaluationCardView.layoutSubviews
            .rectangleForNextRow(sectionTopGap, this.evaluationCard)
            .assignedAsFrameOfView(this.evaluationCard)
            
            // Messaging section (absent from the view tree while messaging is hidden)
            .IF(this.messageThreadView.superview === this)
            .rectangleForNextRow(sectionTopGap, sectionTitleHeight)
            .assignedAsFrameOfView(this.messagingSectionLabel)
            
            .rectangleForNextRow(
                RHSpacing.small,
                320
            )
            .assignedAsFrameOfView(this.messageThreadView)
            .ENDIF()
            
            // Footer
            .rectangleForNextRow(
                RHSpacing.large,
                RHButtons.compactHeight
            )
            .rectangleWithMaxWidth(
                RHOfferDetailDialogViewTokens.footerButtonWidth * 3
                + RHSpacing.large * 2,
                0.5
            )
            .distributeViewsAlongWidth(
                [this.prevButton, this.closeButton, this.nextButton],
                undefined,
                RHSpacing.large,
                [
                    RHOfferDetailDialogViewTokens.footerButtonWidth,
                    RHOfferDetailDialogViewTokens.footerButtonWidth,
                    RHOfferDetailDialogViewTokens.footerButtonWidth
                ]
            )
            .rectangleForNextRow(0, RHSpacing.large)
            .assignedAsFrameOfView(this.footerBottomSpacerView)
    }
    
}
